/**
 * useFlowStore — Session flows (templates + execution state).
 *
 * Storage: 'flows' + 'flow_templates' (collections), 'flow_draft' (single).
 * State machine: idle → running → paused → complete
 * Methods: createFlow, saveAsTemplate, startExecution, pauseExecution, nextStep, completeExecution
 * Cross-store: reads useTimerStore during execution
 *
 * CUTOVER (phase2 §8.1, §11): the authoritative run now lives in the cloud
 * `users/{uid}/_singles/circuit_run` doc (written only by the circuitRun
 * callable). `useCircuitRunStore.bridgeToFlowStore` mirrors that doc into the
 * exec-derived fields below (activeFlow / currentStepIndex / executionState /
 * flowPhase / flowIsPaused / flowTimeRemaining / flowActiveTaskId) so the
 * existing UI keeps working. The old local `flow_execution` single is RETIRED:
 * it is no longer written (no dual-write / two run models) and no longer
 * rehydrated (a stale single must not fight the cloud doc). The exec fields are
 * plain in-memory state populated by the bridge.
 */
import { create } from 'zustand';
import { storage, onRehydrate } from '../utils/storage';

const FLOWS_KEY = 'flows';
const TEMPLATES_KEY = 'flow_templates';
// Retired local run single — kept only as a key to CLEAR any pre-cutover value.
const FLOW_EXEC_KEY = 'flow_execution';
const FLOW_DRAFT_KEY = 'flow_draft';

const useFlowStore = create((set, get) => ({
    // Saved flows & templates
    flows: storage.get(FLOWS_KEY, []),
    templates: storage.get(TEMPLATES_KEY, []),

    // Current working flow (being edited) — from the local draft only.
    currentFlow: storage.get(FLOW_DRAFT_KEY, null) || null,

    // Execution state — populated by useCircuitRunStore.bridgeToFlowStore from
    // the cloud run. Start idle; never restored from a local single.
    activeFlow: null,
    currentStepIndex: 0,
    executionState: 'idle',
    // Timestamp for timer recovery in FlowRunner
    lastSavedAt: null,
    flowTimeRemaining: null,
    flowPhase: 'focus',
    flowIsPaused: false,
    flowActiveTaskId: null,

    // Retired: the cloud circuit_run doc is the single source of truth, mirrored
    // in by the bridge. We intentionally do NOT write the local flow_execution
    // single anymore (kept as a no-op so existing callers/tick paths are safe).
    _persistExec() { /* no-op — cloud run is authoritative (see header) */ },

    // Persist draft building state (the builder draft is still local-only).
    _persistDraft() {
        const s = get();
        if (s.executionState === 'idle') {
            if (s.currentFlow) {
                storage.set(FLOW_DRAFT_KEY, s.currentFlow);
            } else {
                storage.remove(FLOW_DRAFT_KEY);
            }
        }
        // While a run is active the draft is not persisted here; the cloud run
        // owns the active-run snapshot and the draft is untouched.
    },

    setFlowTimer(timeRemaining, phase) {
        set({ flowTimeRemaining: timeRemaining, flowPhase: phase });
        // Persist every 5 seconds
        if (timeRemaining % 5 === 0) get()._persistExec();
    },

    addFlowTime(seconds) {
        set(s => ({
            flowTimeRemaining: Math.max(0, (s.flowTimeRemaining || 0) + seconds),
        }));
        get()._persistExec();
    },

    toggleFlowPause() {
        set(s => ({ flowIsPaused: !s.flowIsPaused }));
        get()._persistExec();
    },

    setFlowActiveTask(taskId) {
        set({ flowActiveTaskId: taskId });
        get()._persistExec();
    },

    // ===== Flow CRUD =====
    createFlow(name = 'New Flow') {
        const flow = {
            id: crypto.randomUUID(),
            name,
            steps: [],
            createdAt: new Date().toISOString(),
        };
        set({ currentFlow: flow });
        get()._persistDraft();
        return flow;
    },

    setCurrentFlow(flow) {
        set({ currentFlow: flow });
        get()._persistDraft();
    },

    updateCurrentFlow(updates) {
        set(state => ({
            currentFlow: state.currentFlow
                ? { ...state.currentFlow, ...updates }
                : null,
        }));
        get()._persistDraft();
    },

    // ===== Step CRUD =====
    addStep(step) {
        const newStep = {
            id: crypto.randomUUID(),
            identityId: null,
            projectId: null,
            taskId: null,          // Legacy single-task (backward compat)
            taskIds: [],           // NEW: multi-task per session
            prepDuration: 2 * 60,
            focusDuration: 50 * 60,
            restDuration: 5 * 60,
            reflectDuration: 5 * 60,
            tags: [],
            ...step,
        };
        // Migrate legacy taskId into taskIds if present
        if (newStep.taskId && !newStep.taskIds.includes(newStep.taskId)) {
            newStep.taskIds = [newStep.taskId, ...newStep.taskIds];
        }
        set(state => {
            if (!state.currentFlow) return {};
            return {
                currentFlow: {
                    ...state.currentFlow,
                    steps: [...state.currentFlow.steps, newStep],
                },
            };
        });
        get()._persistDraft();
        return newStep;
    },

    updateStep(stepId, updates) {
        set(state => {
            if (!state.currentFlow) return {};
            return {
                currentFlow: {
                    ...state.currentFlow,
                    steps: state.currentFlow.steps.map(s =>
                        s.id === stepId ? { ...s, ...updates } : s
                    ),
                },
            };
        });
        get()._persistDraft();
    },

    removeStep(stepId) {
        set(state => {
            if (!state.currentFlow) return {};
            return {
                currentFlow: {
                    ...state.currentFlow,
                    steps: state.currentFlow.steps.filter(s => s.id !== stepId),
                },
            };
        });
        get()._persistDraft();
    },

    insertStepAt(index, step = {}) {
        const newStep = {
            id: crypto.randomUUID(),
            identityId: null,
            projectId: null,
            taskId: null,          // Legacy single-task (backward compat)
            taskIds: [],           // NEW: multi-task per session
            prepDuration: 2 * 60,
            focusDuration: 50 * 60,
            restDuration: 5 * 60,
            reflectDuration: 5 * 60,
            tags: [],
            ...step,
        };
        // Migrate legacy taskId into taskIds if present
        if (newStep.taskId && !newStep.taskIds.includes(newStep.taskId)) {
            newStep.taskIds = [newStep.taskId, ...newStep.taskIds];
        }
        set(state => {
            if (!state.currentFlow) return {};
            const steps = [...state.currentFlow.steps];
            // Clamp index to valid range [0, steps.length]
            const clampedIndex = Math.max(0, Math.min(index, steps.length));
            steps.splice(clampedIndex, 0, newStep);
            return {
                currentFlow: {
                    ...state.currentFlow,
                    steps,
                },
            };
        });
        get()._persistDraft();
        return newStep;
    },

    reorderSteps(fromIndex, toIndex) {
        set(state => {
            if (!state.currentFlow) return {};
            const steps = [...state.currentFlow.steps];
            const [moved] = steps.splice(fromIndex, 1);
            steps.splice(toIndex, 0, moved);
            return {
                currentFlow: {
                    ...state.currentFlow,
                    steps,
                },
            };
        });
        get()._persistDraft();
    },

    // ===== Multi-task helpers =====
    addTaskToStep(stepId, taskId) {
        set(state => {
            if (!state.currentFlow) return {};
            return {
                currentFlow: {
                    ...state.currentFlow,
                    steps: state.currentFlow.steps.map(s => {
                        if (s.id !== stepId) return s;
                        const ids = s.taskIds || [];
                        if (ids.includes(taskId)) return s;
                        return { ...s, taskIds: [...ids, taskId] };
                    }),
                },
            };
        });
        get()._persistDraft();
    },

    removeTaskFromStep(stepId, taskId) {
        set(state => {
            if (!state.currentFlow) return {};
            return {
                currentFlow: {
                    ...state.currentFlow,
                    steps: state.currentFlow.steps.map(s => {
                        if (s.id !== stepId) return s;
                        return { ...s, taskIds: (s.taskIds || []).filter(id => id !== taskId) };
                    }),
                },
            };
        });
        get()._persistDraft();
    },

    // ===== Templates =====
    saveAsTemplate(name) {
        const { currentFlow, templates } = get();
        if (!currentFlow) return;
        const template = {
            id: crypto.randomUUID(),
            name: name || currentFlow.name,
            steps: currentFlow.steps.map(s => ({ ...s, id: crypto.randomUUID() })),
            createdAt: new Date().toISOString(),
        };
        const updated = [...templates, template];
        storage.set(TEMPLATES_KEY, updated);
        set({ templates: updated });
        return template;
    },

    loadTemplate(templateId) {
        const { templates } = get();
        const template = templates.find(t => t.id === templateId);
        if (!template) return;
        const flow = {
            id: crypto.randomUUID(),
            name: template.name,
            steps: template.steps.map(s => ({ ...s, id: crypto.randomUUID() })),
            createdAt: new Date().toISOString(),
        };
        set({ currentFlow: flow });
        get()._persistDraft();
        return flow;
    },

    deleteTemplate(templateId) {
        set(state => {
            const updated = state.templates.filter(t => t.id !== templateId);
            storage.set(TEMPLATES_KEY, updated);
            return { templates: updated };
        });
    },

    // ===== Saved Circuit library (collection 'flows') =====
    saveCircuit() {
        const { currentFlow, flows } = get();
        if (!currentFlow) return null;
        const now = Date.now();
        const exists = flows.some(f => f.id === currentFlow.id);
        const record = {
            ...currentFlow,
            createdAt: currentFlow.createdAt || new Date().toISOString(),
            updatedAt: now,
        };
        const next = exists
            ? flows.map(f => (f.id === record.id ? { ...f, ...record } : f))
            : [...flows, record];
        storage.set(FLOWS_KEY, next);
        set({ flows: next });
        return record;
    },

    updateCircuit(id, updates) {
        set(state => {
            const next = state.flows.map(f =>
                f.id === id ? { ...f, ...updates, updatedAt: Date.now() } : f
            );
            storage.set(FLOWS_KEY, next);
            return { flows: next };
        });
    },

    deleteCircuit(id) {
        set(state => {
            const next = state.flows.filter(f => f.id !== id);
            storage.set(FLOWS_KEY, next);
            return { flows: next };
        });
    },

    duplicateCircuit(id) {
        const { flows } = get();
        const src = flows.find(f => f.id === id);
        if (!src) return null;
        const copy = {
            ...src,
            id: crypto.randomUUID(),
            name: `${src.name} (copy)`,
            steps: (src.steps || []).map(s => ({ ...s, id: crypto.randomUUID() })),
            createdAt: new Date().toISOString(),
            updatedAt: Date.now(),
        };
        const next = [...flows, copy];
        storage.set(FLOWS_KEY, next);
        set({ flows: next });
        return copy;
    },

    loadCircuit(id) {
        const { flows } = get();
        const src = flows.find(f => f.id === id);
        if (!src) return null;
        const flow = {
            ...src,
            steps: (src.steps || []).map(s => ({ ...s })),
        };
        set({ currentFlow: flow });
        get()._persistDraft();
        return flow;
    },

    // ===== Execution =====
    // Local, in-memory-only run bootstrap. The cloud circuit_run doc + bridge
    // are the normal source of truth; this is retained ONLY as an offline/
    // failure fallback so a run started while the callable is unreachable stays
    // usable. It never persists the retired flow_execution single.
    startExecution() {
        const { currentFlow } = get();
        if (!currentFlow || currentFlow.steps.length === 0) return;

        // Auto-set active task from first step's taskIds
        const firstStep = currentFlow.steps[0];
        const taskIds = firstStep?.taskIds || (firstStep?.taskId ? [firstStep.taskId] : []);
        const autoActiveTask = taskIds.length > 0 ? taskIds[0] : null;

        set({
            activeFlow: { ...currentFlow },
            currentStepIndex: 0,
            executionState: 'running',
            flowTimeRemaining: null,
            flowPhase: 'focus',
            flowActiveTaskId: autoActiveTask,
        });
        // No local single write — cloud run is authoritative (see file header).
    },

    pauseToEdit() {
        set({ executionState: 'paused-editing' });
        get()._persistExec();
    },

    resumeExecution() {
        set(state => ({
            executionState: 'running',
            activeFlow: state.currentFlow
                ? { ...state.currentFlow }
                : state.activeFlow,
        }));
        get()._persistExec();
    },

    completeStep() {
        set(state => {
            const nextIndex = state.currentStepIndex + 1;
            if (nextIndex >= (state.activeFlow?.steps?.length || 0)) {
                storage.remove(FLOW_EXEC_KEY);
                // On complete, save the finished flow as the ongoing draft so it stays visible
                if (state.currentFlow) {
                    storage.set(FLOW_DRAFT_KEY, state.currentFlow);
                }
                return {
                    executionState: 'idle',
                    currentStepIndex: 0,
                    activeFlow: null,
                    flowTimeRemaining: null,
                    flowPhase: 'focus',
                    flowActiveTaskId: null,
                };
            }
            // Auto-set active task for next step
            const nextStep = state.activeFlow?.steps?.[nextIndex];
            const nextTaskIds = nextStep?.taskIds || (nextStep?.taskId ? [nextStep.taskId] : []);
            return { currentStepIndex: nextIndex, flowTimeRemaining: null, flowPhase: 'focus', flowActiveTaskId: nextTaskIds.length > 0 ? nextTaskIds[0] : null };
        });
        if (get().executionState !== 'idle') {
            get()._persistExec();
        }
    },

    skipStep() {
        get().completeStep();
    },

    stopExecution() {
        set({
            executionState: 'idle',
            currentStepIndex: 0,
            activeFlow: null,
            flowTimeRemaining: null,
            flowPhase: 'focus',
            flowActiveTaskId: null,
        });
        storage.remove(FLOW_EXEC_KEY);
    },

    getCurrentStep() {
        const { activeFlow, currentStepIndex } = get();
        if (!activeFlow) return null;
        return activeFlow.steps[currentStepIndex] || null;
    },

    isLastStep() {
        const { activeFlow, currentStepIndex } = get();
        if (!activeFlow) return false;
        return currentStepIndex >= activeFlow.steps.length - 1;
    },
}));

// Re-read from localStorage after cloud sync completes.
// CUTOVER: only the flows library, templates, and the local builder draft are
// rehydrated here. Execution state is owned by the cloud circuit_run doc and
// mirrored in by useCircuitRunStore.bridgeToFlowStore — we deliberately do NOT
// resurrect a (possibly stale) local flow_execution single, which would fight
// the cloud doc. Any leftover pre-cutover single is cleared once.
let _clearedLegacyExec = false;
onRehydrate(() => {
    useFlowStore.setState({
        flows: storage.get(FLOWS_KEY, []),
        templates: storage.get(TEMPLATES_KEY, []),
        currentFlow: storage.get(FLOW_DRAFT_KEY, null) || null,
    });

    // One-time cleanup of any retired local run single left by an older build,
    // so it can never be picked up by anything downstream.
    if (!_clearedLegacyExec && storage.get(FLOW_EXEC_KEY, null)) {
        storage.remove(FLOW_EXEC_KEY);
        _clearedLegacyExec = true;
    }
});

export default useFlowStore;
